<template>
  <div>
    <!-- 1) Выбор часового пояса -->
    <div>
      <select
          v-model="selectedTimeZone"
          @change="setTimeZone"
          class="my-5 float-right bg-gray-50 border border-gray-300 text-gray-900
               text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500
               block p-2.5 dark:bg-gray-700 dark:border-gray-600
               dark:placeholder-gray-400 dark:text-white
               dark:focus:ring-blue-500 dark:focus:border-blue-500"
      >
        <option value="Asia/Shanghai">Asia/Shanghai</option>
        <option value="Europe/Kiev">Europe/Kiev</option>
      </select>
    </div>
    <div class="clear-both"></div>


    <!-- 2) Выбор даты (диапазона) -->
    <div class="date-picker">
      <VueDatepicker
          v-model="date"
          range
          :enableTimePicker="false"
          multi-calendars
          @update:model-value="handleDateChange"
      />
    </div>

    <!-- 3) Радиокнопки (Original/Convert) + (Gross/Net) -->
    <div class="flex space-x-6 my-4">
      <div>
        <label class="mr-4">
          <input
              type="radio"
              value="original"
              v-model="baseMetric"
              class="form-radio text-blue-600"
          />
          Original
        </label>
        <label>
          <input
              type="radio"
              value="convert"
              v-model="baseMetric"
              class="form-radio text-blue-600"
          />
          USD
        </label>
      </div>
      <div>
        <label class="mr-4">
          <input
              type="radio"
              value="gross"
              v-model="feeMetric"
              class="form-radio text-blue-600"
          />
          Gross
        </label>
        <label>
          <input
              type="radio"
              value="net"
              v-model="feeMetric"
              class="form-radio text-blue-600"
          />
          Net
        </label>
      </div>
    </div>

    <!-- 1.1) Чекбоксы для выборки процессингов + Кнопка Filter -->
    <div class="checkboxes my-5 flex flex-col">
      <div class="flex flex-wrap space-x-4 checkboxes-list">
        <label
            v-for="processing in activeProcessings"
            :key="processing.id"
            class="inline-flex items-center mr-6 mb-2"
        >
          <input
              type="checkbox"
              :value="processing.id"
              v-model="tempSelectedProcessings"
              class="form-checkbox h-5 w-5 text-blue-600"
          />
          <span class="ml-2 text-gray-700 dark:text-gray-300">{{ processing.name }}</span>
        </label>
      </div>
      <button
          @click="applyFilter"
          :disabled="isLoading"
          class="filter-processing-btn bg-blue-500 text-white px-4 py-2 rounded btn-indigo"
      >
        {{ isLoading ? 'Applying...' : 'Filter' }}
      </button>
    </div>

    <!-- 4) Таблица -->
    <div class="bg-white rounded-md shadow overflow-x-auto">
      <table class="w-full whitespace-nowrap">
        <thead>
        <tr class="text-left font-bold">
          <th class="pb-4 pt-6 px-6">Processing</th>
          <!-- Week Total -->
          <th class="pb-4 pt-6 px-6">{{ combinedMetricLabel }} (Total)</th>
          <!-- Month Total -->
          <th class="pb-4 pt-6 px-6">{{ combinedMetricLabel }} (Month)</th>
          <!-- По дням -->
          <th
              v-for="date in displayedDates"
              :key="date"
              class="pb-4 pt-6 px-6"
          >
            {{ date }}
          </th>
        </tr>
        </thead>
        <tbody>
        <tr
            v-for="(data, processingName) in filteredStatistics"
            :key="processingName"
        >
          <td class="border-t px-6 py-4">
            <a
                :href="'/transactions/?processingId=' + data.id"
                class="flex items-center focus:text-indigo-500"
            >
              {{ processingName }}
            </a>
          </td>
          <!-- Итог за неделю (Week) -->
          <td class="border-t px-6 py-4">
            {{ getTotalsWeekSelected(processingName) }}
          </td>
          <!-- Итог за месяц (Month) -->
          <td class="border-t px-6 py-4">
            {{ getTotalsMonthSelected(processingName) }}
          </td>
          <!-- По дням -->
          <td
              v-for="date in displayedDates"
              :key="date"
              class="border-t px-6 py-4"
          >
            {{ getDateValueSelected(data, date) }}
          </td>
        </tr>
        </tbody>
        <tfoot>
        <tr class="font-bold text-left">
          <td class="border-t px-6 py-4">Total</td>

          <!-- Итоговая сумма (Week) -->
          <td class="border-t px-6 py-4">
            {{ getTotalsWeekTotalSelected() }}
          </td>

          <!-- Итоговая сумма (Month) -->
          <td class="border-t px-6 py-4">
            {{ getTotalsMonthTotalSelected() }}
          </td>

          <!-- Итоги по дням -->
          <td
              v-for="date in displayedDates"
              :key="date"
              class="border-t px-6 py-4"
          >
            {{ getByDayTotalSelected(date) }}
          </td>
        </tr>
        </tfoot>
      </table>

      <!-- 5) График (Chart.js) -->
      <div class="my-8 chart-container">
        <canvas ref="chartCanvas"></canvas>
      </div>
    </div>
  </div>
</template>

<script>
import { Head } from '@inertiajs/inertia-vue3'
import { Inertia } from '@inertiajs/inertia'
import Layout from '@/Shared/Layout'
import Cookies from 'js-cookie'
import VueDatepicker from '@vuepic/vue-datepicker'
import '@vuepic/vue-datepicker/dist/main.css'

import { Chart, registerables } from 'chart.js'
Chart.register(...registerables)

export default {
  components: {
    Head,
    VueDatepicker
  },
  layout: Layout,

  props: {
    // Основные статистики по процессингам
    statistics: { type: Object, required: true },

    // Активные процессинги для чекбоксов
    activeProcessings: { type: Array, required: true },
    selectedProcessings: { type: Array, default: () => [] },

    // By Day (4 отдельных поля)
    totalsByDayGrossOriginal: { type: Object, default: () => ({}) },
    totalsByDayNetOriginal: { type: Object, default: () => ({}) },
    totalsByDayGrossConvert: { type: Object, default: () => ({}) },
    totalsByDayNetConvert: { type: Object, default: () => ({}) },

    // Week (4 поля)
    totalsWeekGrossOriginal: { type: Object, default: () => ({}) },
    totalsWeekNetOriginal: { type: Object, default: () => ({}) },
    totalsWeekGrossConvert: { type: Object, default: () => ({}) },
    totalsWeekNetConvert: { type: Object, default: () => ({}) },

    // Month (4 поля)
    totalsMonthGrossOriginal: { type: Object, default: () => ({}) },
    totalsMonthNetOriginal: { type: Object, default: () => ({}) },
    totalsMonthGrossConvert: { type: Object, default: () => ({}) },
    totalsMonthNetConvert: { type: Object, default: () => ({}) },

    // Суммарные строки
    totalsWeekTotalGrossOriginal: { type: String, default: '0.00' },
    totalsWeekTotalNetOriginal: { type: String, default: '0.00' },
    totalsWeekTotalGrossConvert: { type: String, default: '0.00' },
    totalsWeekTotalNetConvert: { type: String, default: '0.00' },

    totalsMonthTotalGrossOriginal: { type: String, default: '0.00' },
    totalsMonthTotalNetOriginal: { type: String, default: '0.00' },
    totalsMonthTotalGrossConvert: { type: String, default: '0.00' },
    totalsMonthTotalNetConvert: { type: String, default: '0.00' },

    totalsDayTotalGrossOriginal: { type: String, default: '0.00' },
    totalsDayTotalNetOriginal: { type: String, default: '0.00' },
    totalsDayTotalGrossConvert: { type: String, default: '0.00' },
    totalsDayTotalNetConvert: { type: String, default: '0.00' }
  },

  data() {
    // Определяем "сегодня" и "сегодня - 7 дней"
    const endDate = new Date()
    const startDate = new Date()
    startDate.setDate(startDate.getDate() - 7)

    return {
      // Сразу показываем период "за неделю" в VueDatepicker
      date: [startDate, endDate],

      selectedTimeZone: Cookies.get('timeZone') || 'Asia/Shanghai',
      baseMetric: 'original',
      feeMetric: 'gross',

      chart: null,

      // Выбранные процессинги, отражающие текущее состояние на сервере
      currentSelectedProcessings: [],
      // Временные выбранные процессинги, изменяемые пользователем
      tempSelectedProcessings: [],

      // Loading state for filter button
      isLoading: false
    }
  },

  computed: {
    // Собираем все даты, упоминающиеся в statistics
    displayedDates() {
      const allDates = new Set()
      for (const pName in this.statistics) {
        const pData = this.statistics[pName]
        if (pData.dateCurrencyGrossOriginal) {
          Object.keys(pData.dateCurrencyGrossOriginal).forEach(d => allDates.add(d))
        }
        if (pData.dateCurrencyNetOriginal) {
          Object.keys(pData.dateCurrencyNetOriginal).forEach(d => allDates.add(d))
        }
        if (pData.dateCurrencyGrossConvert) {
          Object.keys(pData.dateCurrencyGrossConvert).forEach(d => allDates.add(d))
        }
        if (pData.dateCurrencyNetConvert) {
          Object.keys(pData.dateCurrencyNetConvert).forEach(d => allDates.add(d))
        }
      }
      return [...allDates].sort().reverse()
    },
    combinedMetricLabel() {
      const bm = this.baseMetric === 'original' ? 'Original' : 'USD'
      const fm = this.feeMetric === 'gross' ? 'Gross' : 'Net'
      return `${bm} (${fm})`
    },
    // Фильтруем statistics на основе текущих выбранных процессингов
    filteredStatistics() {
      const filtered = {}
      for (const pName in this.statistics) {
        const pData = this.statistics[pName]
        if (this.currentSelectedProcessings.includes(pData.id)) {
          filtered[pName] = pData
        }
      }
      return filtered
    },
    // Проверяем, изменились ли выбранные процессинги
    hasSelectionChanged() {
      // Compare tempSelectedProcessings with currentSelectedProcessings
      if (this.tempSelectedProcessings.length !== this.currentSelectedProcessings.length) {
        return true
      }
      const sortedTemp = [...this.tempSelectedProcessings].sort()
      const sortedCurrent = [...this.currentSelectedProcessings].sort()
      return !sortedTemp.every((val, index) => val === sortedCurrent[index])
    }
  },

  watch: {
    // При изменении радио / данных / дат - обновляем график
    baseMetric: 'updateChart',
    feeMetric: 'updateChart',
    filteredStatistics: 'updateChart',
    displayedDates: 'updateChart'
  },

  methods: {
    setTimeZone() {
      Cookies.set('timeZone', this.selectedTimeZone, { expires: 365 })
      location.reload()
    },

    handleDateChange(selectedDate) {
      // Убрали автоматическую перезагрузку при изменении даты
      // Теперь даты будут применяться только при нажатии на кнопку Filter
      // Если необходимо, можно добавить валидацию или другие действия
    },

    // Обработчик кнопки Filter
    applyFilter() {
      // Проверяем, изменились ли выбранные процессинги или даты
      if (!this.hasSelectionChanged && !this.hasDateChanged()) {
        return
      }

      this.isLoading = true

      // Получаем даты из date picker
      const [start, end] = this.date
      const startDate = start ? start.toISOString().split('T')[0] : null
      const endDate = end ? end.toISOString().split('T')[0] : null

      // Подготовка параметров для запроса
      const params = {
        processingIds: this.tempSelectedProcessings,
        startDate,
        endDate
      }

      // Удаляем параметры, которые могут быть null
      Object.keys(params).forEach(key => {
        if (params[key] === null || params[key].length === 0) {
          delete params[key]
        }
      })

      // Отправляем запрос с массивом выбранных processings и дат
      Inertia.get('/', params, {
        preserveState: true,
        onSuccess: () => {
          // Обновляем текущее состояние после успешного фильтра
          this.currentSelectedProcessings = [...this.tempSelectedProcessings]
          this.currentDate = [...this.date]
          // Опционально: можно показать уведомление или выполнить другие действия
          console.log(`Filter applied with processing IDs: ${this.currentSelectedProcessings.join(', ')}`)
          console.log(`Date range: ${startDate} to ${endDate}`)
        },
        onError: () => {
          // Обработка ошибок
          console.error('Ошибка при применении фильтра процессингов.')
        },
        onFinish: () => {
          this.isLoading = false
        }
      })
    },

    // Метод для проверки изменений в датах
    hasDateChanged() {
      const [start, end] = this.date
      const currentStart = this.currentDate ? this.currentDate[0] : null
      const currentEnd = this.currentDate ? this.currentDate[1] : null

      const startChanged = start && currentStart ? start.getTime() !== currentStart.getTime() : start !== currentStart
      const endChanged = end && currentEnd ? end.getTime() !== currentEnd.getTime() : end !== currentEnd

      return startChanged || endChanged
    },

    // Методы для таблицы
    getTotalsWeekSelected(processingName) {
      const metric = this.baseMetric
      const fee = this.feeMetric
      if (metric === 'original' && fee === 'gross') {
        return this.totalsWeekGrossOriginal[processingName] || '0.00'
      } else if (metric === 'original' && fee === 'net') {
        return this.totalsWeekNetOriginal[processingName] || '0.00'
      } else if (metric === 'convert' && fee === 'gross') {
        return this.totalsWeekGrossConvert[processingName] || '0.00'
      } else {
        return this.totalsWeekNetConvert[processingName] || '0.00'
      }
    },
    getTotalsMonthSelected(processingName) {
      const metric = this.baseMetric
      const fee = this.feeMetric
      if (metric === 'original' && fee === 'gross') {
        return this.totalsMonthGrossOriginal[processingName] || '0.00'
      } else if (metric === 'original' && fee === 'net') {
        return this.totalsMonthNetOriginal[processingName] || '0.00'
      } else if (metric === 'convert' && fee === 'gross') {
        return this.totalsMonthGrossConvert[processingName] || '0.00'
      } else {
        return this.totalsMonthNetConvert[processingName] || '0.00'
      }
    },
    getDateValueSelected(procData, date) {
      const metric = this.baseMetric
      const fee = this.feeMetric
      if (metric === 'original' && fee === 'gross') {
        return procData.dateCurrencyGrossOriginal?.[date] || '0.00'
      } else if (metric === 'original' && fee === 'net') {
        return procData.dateCurrencyNetOriginal?.[date] || '0.00'
      } else if (metric === 'convert' && fee === 'gross') {
        return procData.dateCurrencyGrossConvert?.[date] || '0.00'
      } else {
        return procData.dateCurrencyNetConvert?.[date] || '0.00'
      }
    },
    getTotalsWeekTotalSelected() {
      const metric = this.baseMetric
      const fee = this.feeMetric
      if (metric === 'original' && fee === 'gross') {
        return this.totalsWeekTotalGrossOriginal
      } else if (metric === 'original' && fee === 'net') {
        return this.totalsWeekTotalNetOriginal
      } else if (metric === 'convert' && fee === 'gross') {
        return this.totalsWeekTotalGrossConvert
      } else {
        return this.totalsWeekTotalNetConvert
      }
    },
    getTotalsMonthTotalSelected() {
      const metric = this.baseMetric
      const fee = this.feeMetric
      if (metric === 'original' && fee === 'gross') {
        return this.totalsMonthTotalGrossOriginal
      } else if (metric === 'original' && fee === 'net') {
        return this.totalsMonthTotalNetOriginal
      } else if (metric === 'convert' && fee === 'gross') {
        return this.totalsMonthTotalGrossConvert
      } else {
        return this.totalsMonthTotalNetConvert
      }
    },
    getByDayTotalSelected(date) {
      const metric = this.baseMetric
      const fee = this.feeMetric
      if (metric === 'original' && fee === 'gross') {
        return this.totalsByDayGrossOriginal[date] || '0.00'
      } else if (metric === 'original' && fee === 'net') {
        return this.totalsByDayNetOriginal[date] || '0.00'
      } else if (metric === 'convert' && fee === 'gross') {
        return this.totalsByDayGrossConvert[date] || '0.00'
      } else {
        return this.totalsByDayNetConvert[date] || '0.00'
      }
    },

    // График (Chart.js)
    initializeChart() {
      if (this.chart) {
        this.chart.destroy()
      }
      const ctx = this.$refs.chartCanvas.getContext('2d')

      const processingNames = Object.keys(this.filteredStatistics)
      const colors = this.generateColors(processingNames.length)

      const datasets = processingNames.map((pName, idx) => {
        const procData = this.filteredStatistics[pName]
        const dataArr = this.displayedDates.map(d => {
          if (this.baseMetric === 'original' && this.feeMetric === 'gross') {
            return parseFloat(procData.dateCurrencyGrossOriginal?.[d] || 0)
          } else if (this.baseMetric === 'original' && this.feeMetric === 'net') {
            return parseFloat(procData.dateCurrencyNetOriginal?.[d] || 0)
          } else if (this.baseMetric === 'convert' && this.feeMetric === 'gross') {
            return parseFloat(procData.dateCurrencyGrossConvert?.[d] || 0)
          } else {
            return parseFloat(procData.dateCurrencyNetConvert?.[d] || 0)
          }
        })
        return {
          label: pName,
          data: dataArr,
          backgroundColor: colors[idx],
          borderColor: colors[idx],
          fill: false,
          tension: 0.1
        }
      })

      this.chart = new Chart(ctx, {
        type: 'line',
        data: {
          labels: this.displayedDates,
          datasets
        },
        options: {
          scales: {
            y: { beginAtZero: true }
          },
          responsive: true,
          maintainAspectRatio: false
        }
      })
    },
    updateChart() {
      this.$nextTick(() => {
        this.initializeChart()
      })
    },
    generateColors(count) {
      const baseColors = [
        'rgba(75, 192, 192, 0.6)',
        'rgba(255, 99, 132, 0.6)',
        'rgba(54, 162, 235, 0.6)',
        'rgba(255, 206, 86, 0.6)',
        'rgba(153, 102, 255, 0.6)',
        'rgba(255, 159, 64, 0.6)'
      ]
      const arr = []
      for (let i = 0; i < count; i++) {
        arr.push(baseColors[i % baseColors.length])
      }
      return arr
    }
  },
  mounted() {
    // Инициализируем текущие и временные выбранные процессинги с ID из props
    if (this.selectedProcessings.length > 0) {
      this.currentSelectedProcessings = [...this.selectedProcessings]
    } else {
      // Если не выбраны, по умолчанию выбираем все активные процессинги
      this.currentSelectedProcessings = this.activeProcessings.map(p => p.id)
    }
    this.tempSelectedProcessings = [...this.currentSelectedProcessings]
    this.currentDate = [...this.date] // Для отслеживания изменений в датах
    this.$nextTick(() => {
      this.initializeChart()
    })
  },
  unmounted() {
    if (this.chart) {
      this.chart.destroy()
    }
  }
}
</script>

<style scoped>
.date-picker {
  max-width: 450px;
  margin-bottom: 10px;
}
.chart-container {
  width: 100%;
  max-width: 1000px;
  margin: 25px auto;
  height: 400px; /* Установите желаемую высоту для графика */
}
.my-4 {
  margin-top: 1rem;
  margin-bottom: 1rem;
}
.checkboxes {
  background-color: #f9fafb;
  padding: 1rem;
  border-radius: 0.5rem;
}
button:disabled {
  cursor: not-allowed;
}
.filter-processing-btn{
  max-width: 100px;
  margin: 10px auto 0;
}
.checkboxes-list{
  margin: 10px auto 0;
}
</style>
